import { Languages } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { type CodigoDeIdioma, NOMBRE_DE_IDIOMA, useIdiomas, useTraducibles } from '../../api/idiomas'
import { clases, claseDeEntrada } from '../../componentes/ui'
import type { Campo } from '../esquema'
import { useFormulario, useValor } from '../Formulario'
import { Envoltura } from './Controles'

/**
 * **Las traducciones de lo que es de la web entera** (CLAUDE.md §37): el eslogan, el plazo de una zona de envío, el
 * nombre de un tipo, la etiqueta de un campo, el texto alternativo de una foto. Qué textos se traducen lo dice el
 * núcleo (`POST /panel/traducibles`, con lo que el documento lleva escrito); aquí se pinta un hueco por texto y por
 * idioma encendido, con el español al lado. Lo que se deja vacío sale en español. No se traduce nada con IA.
 *
 * Se guarda en el campo `traducciones` (`{ en: { <dónde>: <texto> } }`) y el núcleo lo limpia al recibirlo.
 */
type Traducciones = Partial<Record<CodigoDeIdioma, Record<string, string>>>

/** Las traducciones con un texto puesto (o quitado, si se deja vacío): sin idiomas ni objetos vacíos, y `null` si no queda nada. */
export const conTraduccion = (valor: null | Traducciones | undefined, idioma: CodigoDeIdioma, ruta: string, texto: string): null | Traducciones => {
  const siguiente: Traducciones = { ...(valor ?? {}) }
  const delIdioma = { ...(siguiente[idioma] ?? {}) }
  if (texto === '') delete delIdioma[ruta]
  else delIdioma[ruta] = texto
  if (Object.keys(delIdioma).length > 0) siguiente[idioma] = delIdioma
  else delete siguiente[idioma]
  return Object.keys(siguiente).length > 0 ? siguiente : null
}

/** Lo que el documento lleva escrito, estable: se manda al núcleo medio segundo después de dejar de escribir. */
const useEstable = <T,>(valor: T, espera = 500): T => {
  const [estable, setEstable] = useState(valor)
  useEffect(() => {
    const t = setTimeout(() => setEstable(valor), espera)
    return () => clearTimeout(t)
  }, [valor, espera])
  return estable
}

export const EditorDeTraducciones = ({
  sitio,
  coleccion,
  datos,
  valor,
  cambiar,
  soloLectura = false,
  enlaceAIdiomas,
}: {
  sitio: null | number
  coleccion: string
  datos: Record<string, unknown>
  valor: null | Traducciones | undefined
  cambiar: (v: null | Traducciones) => void
  soloLectura?: boolean
  /** La pantalla de Idiomas de la web, para quien todavía no ha encendido ninguno. */
  enlaceAIdiomas?: string
}) => {
  const { data: idiomas } = useIdiomas(sitio)
  const adicionales = (idiomas?.encendidos ?? []).filter((i): i is Exclude<CodigoDeIdioma, 'es'> => i !== 'es')
  const sinTraducciones = useMemo(() => {
    const { traducciones: _fuera, ...resto } = datos
    return resto
  }, [datos])
  const estables = useEstable(sinTraducciones)
  const { data, isPending } = useTraducibles(coleccion, estables, adicionales.length > 0)
  const textos = data?.textos ?? []

  if (!idiomas) return null
  if (adicionales.length === 0) {
    return (
      <p className="text-xs text-tenue">
        Tu web solo está en español.{' '}
        {enlaceAIdiomas ? (
          <Link className="text-violeta underline-offset-2 hover:underline" to={enlaceAIdiomas}>
            Enciende el inglés o el portugués
          </Link>
        ) : (
          'Enciende el inglés o el portugués en Idiomas'
        )}{' '}
        y aquí podrás traducir estos textos.
      </p>
    )
  }

  const escribir = (idioma: CodigoDeIdioma, ruta: string, texto: string) => cambiar(conTraduccion(valor, idioma, ruta, texto))

  if (isPending && textos.length === 0) return <p className="text-xs text-tenue">Buscando los textos que se pueden traducir…</p>
  if (textos.length === 0) return <p className="text-xs text-tenue">Todavía no hay textos que traducir: escribe primero los de español.</p>

  return (
    <div className="flex flex-col gap-3">
      {adicionales.map((idioma) => {
        const hechos = textos.filter((t) => valor?.[idioma]?.[t.ruta]).length
        return (
          <details className="rounded-campo border border-linea bg-papel" key={idioma} open={textos.length <= 12}>
            <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[13px] font-medium text-tinta">
              <Languages aria-hidden className="size-4 text-tenue" />
              {NOMBRE_DE_IDIOMA[idioma]}
              <span className={clases('ml-auto text-xs font-normal', hechos === textos.length ? 'text-ok' : 'text-tenue')}>
                {hechos} de {textos.length} traducidos
              </span>
            </summary>
            <div className="flex flex-col gap-3 border-t border-linea px-3 py-3">
              {textos.map((t) => {
                const id = `traduccion-${idioma}-${t.ruta}`
                const actual = valor?.[idioma]?.[t.ruta] ?? ''
                const esDireccion = t.ruta === '@ruta'
                return (
                  <div className="flex flex-col gap-1" key={t.ruta}>
                    <label className="text-[13px] font-medium text-tinta" htmlFor={id}>
                      {t.etiqueta}
                    </label>
                    <p className="line-clamp-2 text-xs text-tenue">
                      <span className="font-medium">Español:</span> {esDireccion ? `/${t.original}` : t.original}
                    </p>
                    {t.largo ? (
                      <textarea className={clases(claseDeEntrada(), 'h-auto min-h-16 py-2')} disabled={soloLectura} id={id} lang={idioma} onChange={(e) => escribir(idioma, t.ruta, e.target.value)} placeholder="Sin traducir: sale en español" rows={3} value={actual} />
                    ) : (
                      <div className="flex items-center gap-1.5">
                        {esDireccion && <span className="font-mono text-xs text-tenue">/{idioma}/</span>}
                        <input className={clases(claseDeEntrada(), esDireccion && 'font-mono text-[13px]')} disabled={soloLectura} id={id} lang={idioma} onChange={(e) => escribir(idioma, t.ruta, esDireccion ? e.target.value.toLowerCase() : e.target.value)} placeholder={esDireccion ? t.original : 'Sin traducir: sale en español'} value={actual} />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </details>
        )
      })}
    </div>
  )
}

/** El control del motor: el campo `traducciones` de una colección que se comparte entre idiomas. */
export const Traducciones = ({ campo, ruta, soloLectura }: { campo: Campo; ruta: string; soloLectura: boolean }) => {
  const { esquema, espacio, sitio, valores } = useFormulario()
  const { valor, cambiar, error } = useValor<null | Traducciones>(ruta)
  return (
    <Envoltura campo={campo} error={error} ruta={ruta}>
      <EditorDeTraducciones
        cambiar={(v) => cambiar(v)}
        coleccion={esquema.coleccion}
        datos={valores as Record<string, unknown>}
        enlaceAIdiomas={espacio && sitio ? `/e/${espacio}/s/${sitio}/idiomas` : undefined}
        sitio={sitio}
        soloLectura={soloLectura}
        valor={valor}
      />
    </Envoltura>
  )
}
